import * as React from 'react';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { useHistory, useRouteMatch } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { clearCredentials, selectAuthState, switchProfileRequestReceived } from '../sagas/auth/authSlice';
import { useEffect } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Avatar, Box, ClickAwayListener, Grid, IconButton, ListItemIcon, Menu, MenuItem, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { Role } from '@inosio/payments-sdk';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { makeStyles } from '@mui/styles';
import { Link } from 'react-router-dom';
import { Logout } from '@mui/icons-material';
import { camelCaseRemovalRegex } from '../../utils/regex';

export const useStyle = makeStyles(() => ({
  accordionStyle: {
    borderRadius: '24px !important',
    zIndex: 1,
  },
  accordionSummary: {
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
  },
}));

export default function ProfileWidget() {
  const history = useHistory();
  const dispatch = useDispatch();
  const classes = useStyle();
  const { url } = useRouteMatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); //using material ui media query
  const [expanded, setExpanded] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMobileMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMobileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    const { pUuid } = event.currentTarget.dataset;

    if (pUuid) {
      dispatch({
        type: switchProfileRequestReceived.type,
        payload: {
          principalUuid: pUuid,
        },
      });
    }
  };

  const handleLogout = (event: React.MouseEvent<HTMLElement>) => {
    dispatch(clearCredentials());
    history.push('/login');
  };
  const { isSuccess, isError, errorMessage, ipaUser, profileChange, userDetails } = useSelector(selectAuthState);

  const handleClickAway = () => {
    setExpanded(false);
  };

  useEffect(() => {
    if (isSuccess && profileChange) {
      //determine which role this user is:
      const principal = ipaUser?.profiles.find((p) => p.uuid === ipaUser?.currentProfile.uuid);

      if (principal?.role === Role.PropertyManagerAdmin || principal?.role === Role.PropertyManager) {
        //TODO: send them to the pm dashboard
        history.push('/pmui/transactions');
      } else if (principal?.role === Role.Resident) {
        //send them to the resui page
        history.push('/resui/payments');
      } else if (principal?.role === Role.Applicant) {
        //send them to the application page
        history.push('/resui/applications');
      }
    }
  }, [isError, errorMessage, isSuccess, ipaUser, profileChange, dispatch, history]);

  return (
    <Box>
      {/* Render a different component while on Mobile */}
      {isMobile ? (
        <Box>
          <Tooltip title="Account settings">
            <IconButton onClick={handleMobileMenuClick} size="small" sx={{ ml: 2 }} aria-controls={open ? 'account-menu' : undefined} aria-haspopup="true" aria-expanded={open ? 'true' : undefined}>
              <Avatar />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={open}
            onClose={handleMobileMenuClose}
            onClick={handleMobileMenuClose}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: 'auto',
                borderRadius: '10px',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                mt: 1.5,
                '& .MuiAvatar-root': {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
                '&::before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: 'background.paper',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            {userDetails?.Profiles?.map((profile) => {
              return (
                <Box>
                  <MenuItem key={profile.Uuid}>
                    <Grid data-p-uuid={profile.Uuid} onClick={handleMenuClick}>
                      <Grid>
                        <Grid sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography
                            id="ipa-user"
                            sx={{
                              fontFamily: 'Work Sans',
                              fontStyle: 'normal',
                              fontSize: '16px',
                              color: '#A8A8A8',
                              fontWeight: 400,
                              lineHeight: '22px',
                            }}
                          >
                            {profile.Role.replace(camelCaseRemovalRegex, '$1 $2')}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: 'Work Sans',
                              fontStyle: 'normal',
                              fontSize: '16px',
                              fontWeight: 400,
                              color: '#A8A8A8',
                              lineHeight: '22px',
                            }}
                          >
                            {profile.Uuid === userDetails.defaultProfileUuid ? 'Default' : ''}
                          </Typography>
                        </Grid>
                        <Grid item sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                          <Typography
                            sx={{
                              fontFamily: 'Work Sans',
                              fontStyle: 'normal',
                              fontSize: '16px',
                              fontWeight: 400,
                              color: '#14142B',
                              lineHeight: '22px',
                            }}
                          >
                            {profile.DisplayName}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  </MenuItem>
                  <Divider sx={{ width: '100%' }} />
                </Box>
              );
            })}
            <MenuItem>
              <Link to={`${url}/profilesettings`} onClick={() => setExpanded(false)} style={{ textDecoration: 'none', color: '#000' }}>
                <Grid display="flex" justifyContent="center">
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontSize: '16px',
                      color: '#14142B',
                      fontWeight: 400,
                      lineHeight: '22px',
                    }}
                  >
                    Manage accounts
                  </Typography>
                </Grid>
              </Link>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              <Typography
                sx={{
                  fontFamily: 'Work Sans',
                  fontStyle: 'normal',
                  fontSize: '16px',
                  color: '#14142B',
                  fontWeight: 400,
                  lineHeight: '22px',
                }}
              >
                Logout
              </Typography>
            </MenuItem>
          </Menu>
        </Box>
      ) : (
        <React.Fragment>
          <ClickAwayListener onClickAway={handleClickAway}>
            <Grid>
              <Accordion id="profileWidget" className={classes.accordionStyle} expanded={expanded} onChange={() => setExpanded(!expanded)}>
                <AccordionSummary className={classes.accordionSummary} expandIcon={<ExpandMoreIcon />}>
                  <Grid item ml={1}>
                    <Typography
                      id="user-name-id"
                      style={{
                        fontFamily: 'Work Sans',
                        fontStyle: 'normal',
                        fontWeight: 600,
                        fontSize: '18px',
                        color: '#14142B',
                      }}
                    >
                      {userDetails?.Email || userDetails?.Profiles?.[0]?.DisplayName}
                    </Typography>
                  </Grid>
                </AccordionSummary>
                <Divider />
                {userDetails?.Profiles?.map((profile) => {
                  return (
                    <div key={profile.Uuid}>
                      <Grid
                        sx={{
                          '&:hover': {
                            backgroundColor: '#F1F0F5',
                            cursor: 'pointer',
                          },
                        }}
                        data-p-uuid={profile.Uuid}
                        onClick={handleMenuClick}
                      >
                        <Grid p={1}>
                          <Grid sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography
                              id="ipa-user"
                              sx={{
                                fontFamily: 'Work Sans',
                                fontStyle: 'normal',
                                fontSize: '16px',
                                color: '#A8A8A8',
                                fontWeight: 400,
                                lineHeight: '22px',
                              }}
                            >
                              {profile.Role.replace(camelCaseRemovalRegex, '$1 $2')}
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: 'Work Sans',
                                fontStyle: 'normal',
                                fontSize: '16px',
                                fontWeight: 400,
                                color: '#A8A8A8',
                                lineHeight: '22px',
                              }}
                            >
                              {profile.Uuid === userDetails.defaultProfileUuid ? 'Default' : ''}
                            </Typography>
                          </Grid>
                          <Grid item sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                            <Typography
                              sx={{
                                fontFamily: 'Work Sans',
                                fontStyle: 'normal',
                                fontSize: '16px',
                                fontWeight: 400,
                                color: '#14142B',
                                lineHeight: '22px',
                              }}
                            >
                              {profile.DisplayName}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Divider />
                    </div>
                  );
                })}
                <div>
                  <Grid
                    sx={{
                      '&:hover': {
                        backgroundColor: '#F1F0F5',
                        cursor: 'pointer',
                      },
                    }}
                  >
                    <Link to={`${url}/profilesettings`} onClick={() => setExpanded(false)} style={{ textDecoration: 'none', color: '#000' }}>
                      <Grid p={2} display="flex" justifyContent="center">
                        <Typography
                          sx={{
                            fontFamily: 'Work Sans',
                            fontStyle: 'normal',
                            fontSize: '18px',
                            color: '#14142B',
                            fontWeight: 400,
                            lineHeight: '22px',
                          }}
                        >
                          Manage accounts
                        </Typography>
                      </Grid>
                    </Link>
                  </Grid>
                  <Divider />
                </div>
                <AccordionDetails>
                  <Grid
                    p={0.5}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography
                      id="log-out-btn"
                      sx={{
                        fontFamily: 'Work Sans',
                        fontStyle: 'normal',
                        fontSize: '18px',
                        fontWeight: 400,
                        lineHeight: '22px',
                        '&:hover': {
                          cursor: 'pointer',
                        },
                      }}
                      onClick={handleLogout}
                    >
                      Logout
                    </Typography>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </ClickAwayListener>
        </React.Fragment>
      )}
    </Box>
  );
}
